/*
 * Copyright 2024 GLA UK Research and Development Directive
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { DomainService } from 'src/app/modules/shared/services/domain.service';
import { NiordKeycloakService } from 'src/app/modules/shared/services/niord-keycloak.service';

/**
 * The Auth Interceptor
 */
@Injectable({
    providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {

    /**
     * The HTTP Authorisation Interceptor Construstor.
     * 
     * @param router            The angular router
     * @param niordKeycloak   The Niord keycloak service
     * @param domainService     The domain service
     */
    constructor(private router: Router,
                private niordKeycloak: NiordKeycloakService,
                protected domainService: DomainService) {

    }

    /**
     * The interceptor operations
     * 
     * @param request     The outgoing request
     * @param next        The next response
     * @returns the HTTP handler result
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        //Skip local assets for testing
        if (request.url.indexOf('/assets') >= 0 
            || request.url.indexOf('/publish') >= 0
            || request.url.indexOf('/tinymce') >= 0) {
            return next.handle(request);
        }

        // Always point to the Niord Keycloak domain is available
        if (this.domainService.getDomain()) {
            request = request.clone({
                withCredentials: true,
                headers: request.headers.set('NiordDomain', this.domainService.getDomain().domainId)
            });
        }

        // Handle as many error as we can
        return next.handle(request).pipe(
            catchError(error => {
                if (error.status === 401) {
                    console.error('session timeout?');
                    this.niordKeycloak.logout();
                } else if (error.status === 403) {
                    console.error('Forbidden');
                } else if (error.status === 404) {
                    console.error('Not found');
                } else if (error.status) {
                    if (error.data && error.data.errorMessage) {
                        console.error(error.data.errorMessage);
                    } else {
                        console.error("An unexpected server error has occurred " + error.status);
                    }
                } else if (error === "Failed to refresh token") {
                    this.niordKeycloak.logout();
                }

                return throwError(() => error);
            })
        );
    }
}
